/**
 * Output-direction companion to `serialize-input.ts`.
 *
 * Apollo / pg_graphql returns BigFloat columns as **strings** (e.g. `"5000"`).
 * Domain models that type those fields as `number` (e.g. `features/deals`'
 * `Deal`, `offerings`' `Offering`) must coerce on the way IN — otherwise the
 * type lies and `+` silently concatenates (`"5000" + 3000 === "50003000"`),
 * while `*` / `/` only work by accidental JS coercion.
 *
 * This is the read-side mirror of `stringifyDecimalFields` (the write side).
 * It is **idempotent**: numbers pass through unchanged, `null` / `undefined`
 * are preserved, and a non-numeric string is left as-is (never `NaN`). Safe to
 * run on PostgREST rows (already numeric) as well as Apollo rows (strings).
 *
 * The BigFloat field list per table is the single registry in
 * `serialize-input.ts` (`SCALAR_FIELDS.<table>.bigFloat`) — pass it in so this
 * stays schema-driven.
 */
export function coerceBigFloatFieldsToNumber<T extends object>(
  input: T,
  fields: readonly string[]
): T {
  const record = input as Record<string, unknown>;

  return {
    ...input,
    ...Object.fromEntries(
      fields
        .filter((field) => field in record)
        .map((field) => {
          const value = record[field];
          if (value === null || value === undefined) return [field, value];
          const numeric = typeof value === "number" ? value : Number(value);
          return [field, Number.isFinite(numeric) ? numeric : value];
        })
    ),
  } as T;
}
